import { useState } from "react";
import { api, money } from "@/lib/api";
import { MessageCircle, RefreshCw, Ruler } from "lucide-react";
import { toast } from "sonner";

const KEY_STORE = "kc_admin_key";

const STATUS_STYLES = {
  new: "bg-[#2F5D3A]/10 text-[#2F5D3A]",
  texted: "bg-[#1A1A1A]/10 text-[#6E6E69]",
};

const buildMessage = (l) => {
  const blocks = (l.results || []).map((r) => {
    const items = (r.products || [])
      .map((p) => `- ${p.title} (${money(p.price)}) ${window.location.origin}/product/${p.slug}`)
      .join("\n");
    return `${r.function}:\n${items}`;
  });
  return (
    `Hi! This is Kurti Connection — here is your wedding-guest lookbook.\n\n` +
    `Your size: ${l.size_garment} (US ${l.size_us})\n` +
    (l.advice?.length ? l.advice.map((a) => `* ${a}`).join("\n") + "\n" : "") +
    `\n${blocks.join("\n\n")}\n\nEvery product page has our Virtual Try-On — see it on you before you order!\n` +
    `Questions? Just reply here!`
  );
};

// /admin/outfit-leads — Outfit Finder leads with one-click lookbook texting.
export default function AdminOutfitLeads() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORE) || "");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!adminKey) { toast.error("Enter your admin key."); return; }
    localStorage.setItem(KEY_STORE, adminKey);
    setLoading(true);
    try {
      const { data } = await api.get("/outfit-finder/leads", { headers: { "X-Admin-Key": adminKey } });
      setRows(data.leads || []);
    } catch {
      toast.error("Could not load — check your admin key.");
      localStorage.removeItem(KEY_STORE);
    }
    setLoading(false);
  };

  const textLead = async (l) => {
    window.open(`https://wa.me/${l.phone}?text=${encodeURIComponent(buildMessage(l))}`, "_blank", "noopener");
    try {
      await api.post(`/outfit-finder/leads/${l.id}/texted`, {}, { headers: { "X-Admin-Key": adminKey } });
      setRows((prev) => prev.map((x) => (x.id === l.id ? { ...x, status: "texted" } : x)));
    } catch {
      toast.error("WhatsApp opened, but could not mark as texted.");
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-5 py-10 font-body" data-testid="admin-outfit-leads">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="font-display text-3xl flex items-center gap-2">
          <Ruler size={22} className="text-[#B84A3B]" /> Outfit Finder Leads
        </h1>
        <div className="flex items-center gap-2">
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Admin key"
            data-testid="admin-outfit-key"
            className="border border-[#E5E5DF] px-3 py-2 text-sm w-56 focus:outline-none focus:border-[#B84A3B]"
          />
          <button onClick={load} data-testid="admin-outfit-load"
            className="bg-[#1A1A1A] text-white px-4 py-2 text-[0.7rem] font-mono tracking-[0.15em] uppercase hover:bg-[#B84A3B] transition-colors flex items-center gap-2">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Load
          </button>
        </div>
      </div>

      {rows && rows.length === 0 && (
        <p className="mt-10 text-[#6E6E69]" data-testid="admin-outfit-empty">No outfit finder leads yet.</p>
      )}

      {rows && rows.length > 0 && (
        <div className="mt-6 space-y-3">
          {rows.map((l) => (
            <div key={l.id} className="border border-[#E5E5DF] p-4" data-testid={`outfit-lead-${l.id}`}>
              <div className="flex justify-between gap-3 flex-wrap items-start">
                <div>
                  <p className="font-medium text-sm">
                    {l.email && <span data-testid={`outfit-lead-email-${l.id}`}>{l.email}</span>}
                    {l.email && l.phone && <span className="text-[#6E6E69]"> · </span>}
                    {l.phone && <span className="font-mono" data-testid={`outfit-lead-phone-${l.id}`}>+{l.phone}</span>}
                  </p>
                  <p className="text-sm text-[#6E6E69] mt-0.5">
                    {(l.functions || []).map((f) => f.name).join(", ")} · Size <b className="text-[#1A1A1A]">{l.size_garment} (US {l.size_us})</b>
                    <span className="text-xs font-mono ml-2">
                      {l.measurements?.chest}/{l.measurements?.waist}/{l.measurements?.hip}
                    </span>
                  </p>
                  <p className="text-xs font-mono text-[#6E6E69] mt-1">{new Date(l.created_at).toLocaleString()}</p>
                  {l.advice?.length > 0 && (
                    <p className="mt-1.5 text-xs text-[#6E6E69] italic">{l.advice.join(" ")}</p>
                  )}
                </div>
                <div className="flex items-center gap-2.5">
                  <span className={`text-[0.6rem] font-mono tracking-[0.12em] uppercase px-2.5 py-1 ${STATUS_STYLES[l.status] || STATUS_STYLES.new}`}
                    data-testid={`outfit-lead-status-${l.id}`}>
                    {l.status}
                  </span>
                  {l.phone && (
                    <button
                      data-testid={`outfit-lead-text-${l.id}`}
                      onClick={() => textLead(l)}
                      className="bg-[#1FA855] text-white px-3.5 py-2 text-[0.65rem] font-mono tracking-[0.12em] uppercase hover:bg-[#17853f] transition-colors flex items-center gap-1.5"
                    >
                      <MessageCircle size={13} /> Text Lookbook
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
